import React, { useEffect } from 'react';
import { LDContext, LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { SessionData, useSessionData } from '../auth/SessionDataHook';
import { useFlags } from './FlagsHook';
import { useFeatureFlagStore } from './featureFlagStore';

type Props = {
  children: React.ReactNode;
};

export function FeatureFlagProvider({ children }: Props) {
  const { session, isLoading } = useSessionData();
  const context = buildContext(session);

  return (
    <LDProvider
      deferInitialization
      clientSideID={import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID}
      context={context}
    >
      <ContextUpdater context={context} />
      <FlagSyncer />
      {!isLoading && children}
    </LDProvider>
  );
}

function ContextUpdater({ context }: { context: LDContext }) {
  const ldClient = useLDClient();

  useEffect(() => {
    if (ldClient && context) {
      ldClient.identify(context);
    }
  }, [context, ldClient]);

  return null;
}

const setFlagEnabledCustomSettingsFlow =
  useFeatureFlagStore.getState().setFlagEnabledCustomSettingsFlow;

function FlagSyncer() {
  const { flagEnabledCustomSettingsFlow } = useFlags();

  useEffect(() => {
    setFlagEnabledCustomSettingsFlow(flagEnabledCustomSettingsFlow);
  }, [flagEnabledCustomSettingsFlow]);

  return null;
}

function buildContext(session: SessionData | null): LDContext {
  if (!session) {
    return {
      key: 'anonymous',
      kind: 'user-info',
      anonymous: true,
      environment: import.meta.env.MODE,
    };
  }

  const metadata = session.identity?.metadata_public;
  const { tenantId, userId } = metadata;

  return {
    key: userId,
    kind: 'user-info',
    'user-id': userId,
    tenant: tenantId,
    email: session.identity?.traits.email,
    environment: import.meta.env.MODE,
  };
}
